import { findQuoteRange, quoteSearchCandidates } from './pdfQuoteHighlight'

type DomTextSpan = {
  node: Text
  start: number
  end: number
}

function collectTextSpans(root: HTMLElement): { text: string; spans: DomTextSpan[] } {
  const spans: DomTextSpan[] = []
  let text = ''
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let current = walker.nextNode()
  while (current) {
    const node = current as Text
    const content = node.textContent || ''
    if (content) {
      const start = text.length
      text += content
      spans.push({ node, start, end: text.length })
    }
    current = walker.nextNode()
  }
  return { text, spans }
}

function splitAndWrap(textNode: Text, localStart: number, localEnd: number): HTMLElement | null {
  const text = textNode.textContent || ''
  if (localEnd <= localStart) return null
  const before = text.slice(0, localStart)
  const mid = text.slice(localStart, localEnd)
  const after = text.slice(localEnd)
  const parent = textNode.parentNode
  if (!parent || !mid) return null

  const mark = document.createElement('mark')
  mark.className = 'doc-quote-highlight-mark'
  mark.textContent = mid

  if (before) parent.insertBefore(document.createTextNode(before), textNode)
  parent.insertBefore(mark, textNode)
  if (after) parent.insertBefore(document.createTextNode(after), textNode)
  parent.removeChild(textNode)
  return mark
}

function applyHighlight(spans: DomTextSpan[], start: number, end: number): HTMLElement[] {
  const marks: HTMLElement[] = []
  const affected = spans.filter((s) => s.end > start && s.start < end)
  for (let i = affected.length - 1; i >= 0; i--) {
    const span = affected[i]!
    const localStart = Math.max(0, start - span.start)
    const localEnd = Math.min(span.node.length, end - span.start)
    const mark = splitAndWrap(span.node, localStart, localEnd)
    if (mark) marks.unshift(mark)
  }
  return marks
}

export function clearQuoteHighlights(root: HTMLElement): void {
  root.querySelectorAll('mark.doc-quote-highlight-mark').forEach((el) => {
    const parent = el.parentNode
    if (!parent) return
    parent.replaceChild(document.createTextNode(el.textContent || ''), el)
    parent.normalize()
  })
  delete root.dataset.quoteHighlight
}

export function selectHighlightMarks(marks: HTMLElement[]): void {
  if (!marks.length) return
  marks[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  try {
    const range = document.createRange()
    range.setStartBefore(marks[0]!)
    range.setEndAfter(marks[marks.length - 1]!)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  } catch {
    /* 部分浏览器对 mark 选区限制，保留标红即可 */
  }
}

function existingMarks(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll('mark.doc-quote-highlight-mark'))
}

/** 在 HTML 容器内标红 quote 并保持选中，成功返回 true */
export function highlightQuoteInElement(root: HTMLElement, quote: string | null | undefined): boolean {
  const q = (quote || '').trim()
  if (!q) return false

  if (root.dataset.quoteHighlight === q) {
    const marks = existingMarks(root)
    if (marks.length) {
      selectHighlightMarks(marks)
      return true
    }
  }

  clearQuoteHighlights(root)

  const built = collectTextSpans(root)
  for (const cand of quoteSearchCandidates(q)) {
    const range = findQuoteRange(built.text, cand)
    if (!range) continue
    const marks = applyHighlight(built.spans, range[0], range[1])
    if (marks.length) {
      root.dataset.quoteHighlight = q
      selectHighlightMarks(marks)
      return true
    }
  }
  return false
}
